import { RefObject, useEffect, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import { toast } from "react-toastify"
import * as XLSX from "xlsx"
import { useSelector } from "react-redux"
import Table from "../../../core/common/dataTable/index"
import TooltipOption from "../../../core/common/tooltipOption"
import { TableData } from "../../../core/data/interface"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import {
  Book,
  BookItem,
  getBookById,
  getBookItems,
  getBookItemByBarcode,
} from "../../../service/books"
import { all_routes } from "../../router/all_routes"
import ReturnBookModal from "./ReturnBookModal"

const BOOK_ITEMS_SORT_OPTIONS = [
  {
    label: "Newest First",
    value: { sortBy: "createdAt", sortOrder: "desc" as const },
  },
  {
    label: "Oldest First",
    value: { sortBy: "createdAt", sortOrder: "asc" as const },
  },
  {
    label: "Barcode (A->Z)",
    value: { sortBy: "barcode", sortOrder: "asc" as const },
  },
  {
    label: "Barcode (Z->A)",
    value: { sortBy: "barcode", sortOrder: "desc" as const },
  },
]

const statusOptions = [
  { value: "", label: "All" },
  { value: "available", label: "Available" },
  { value: "issued", label: "Issued" },
  { value: "lost", label: "Lost" },
  { value: "damaged", label: "Damaged" },
]

const CODE39_PATTERNS: Record<string, string> = {
  "0": "nnnwwnwnn",
  "1": "wnnwnnnnw",
  "2": "nnwwnnnnw",
  "3": "wnwwnnnnn",
  "4": "nnnwwnnnw",
  "5": "wnnwwnnnn",
  "6": "nnwwwnnnn",
  "7": "nnnwnnwnw",
  "8": "wnnwnnwnn",
  "9": "nnwwnnwnn",
  A: "wnnnnwnnw",
  B: "nnwnnwnnw",
  C: "wnwnnwnnn",
  D: "nnnnwwnnw",
  E: "wnnnwwnnn",
  F: "nnwnwwnnn",
  G: "nnnnnwwnw",
  H: "wnnnnwwnn",
  I: "nnwnnwwnn",
  J: "nnnnwwwnn",
  K: "wnnnnnnww",
  L: "nnwnnnnww",
  M: "wnwnnnnwn",
  N: "nnnnwnnww",
  O: "wnnnwnnwn",
  P: "nnwnwnnwn",
  Q: "nnnnnnwww",
  R: "wnnnnnwwn",
  S: "nnwnnnwwn",
  T: "nnnnwnwwn",
  U: "wwnnnnnnw",
  V: "nwwnnnnnw",
  W: "wwwnnnnnn",
  X: "nwnnwnnnw",
  Y: "wwnnwnnnn",
  Z: "nwwnwnnnn",
  "-": "nwnnnnwnw",
  ".": "wwnnnnwnn",
  " ": "nwwnnnwnn",
  $: "nwnwnwnnn",
  "/": "nwnwnnnwn",
  "+": "nwnnnwnwn",
  "%": "nnnwnwnwn",
  "*": "nwnnwnwnn", // start/stop
}

const sanitizeCode39 = (raw: string) => {
  const normalized = String(raw || "")
    .trim()
    .replace(/_/g, "-")
    .toUpperCase()

  // Code39 supports: A-Z 0-9 space - . $ / + %
  const isValid = /^[0-9A-Z \-.\$\/\+%]*$/.test(normalized)
  return { value: normalized, isValid }
}

const getBackendBarcodeValue = (item: BookItem | null) => {
  if (!item) return ""
  return String((item as any)?.barcode || "").trim()
}

const Code39BarcodeSvg = ({
  value,
  height = 80,
  unit = 2,
  showText = true,
  svgRef,
}: {
  value: string
  height?: number
  unit?: number
  showText?: boolean
  svgRef?: RefObject<SVGSVGElement | null>
}) => {
  const { value: clean, isValid } = sanitizeCode39(value)
  const content = `*${clean}*`

  // If invalid, render placeholder
  if (!isValid) {
    return (
      <div className="alert alert-warning mb-0">
        Barcode can’t be generated for this value. Allowed characters: A-Z, 0-9,
        space, <code>-</code>, <code>.</code>, <code>$</code>, <code>/</code>,{" "}
        <code>+</code>, <code>%</code>.
      </div>
    )
  }

  // Build bars as SVG rects
  const narrow = 1 * unit
  const wide = 3 * unit
  const interCharGap = 1 * unit
  const quietZone = 6 * unit
  const barHeight = height
  const textHeight = showText ? 18 : 0

  let x = quietZone
  const rects: Array<{ x: number; w: number }> = []

  for (let ci = 0; ci < content.length; ci++) {
    const ch = content[ci]
    const pattern = CODE39_PATTERNS[ch]
    if (!pattern) continue

    for (let i = 0; i < pattern.length; i++) {
      const isBar = i % 2 === 0
      const w = pattern[i] === "w" ? wide : narrow
      if (isBar) rects.push({ x, w })
      x += w
    }
    // gap between characters (space)
    x += interCharGap
  }

  const totalWidth = x + quietZone
  const totalHeight = barHeight + textHeight

  return (
    <svg
      ref={svgRef as any}
      xmlns="http://www.w3.org/2000/svg"
      width={totalWidth}
      height={totalHeight}
      viewBox={`0 0 ${totalWidth} ${totalHeight}`}
      role="img"
      aria-label={`Barcode for ${clean}`}
    >
      <rect x="0" y="0" width={totalWidth} height={totalHeight} fill="#fff" />
      {rects.map((r, idx) => (
        <rect
          key={idx}
          x={r.x}
          y={0}
          width={r.w}
          height={barHeight}
          fill="#000"
        />
      ))}
      {showText && (
        <text
          x={totalWidth / 2}
          y={barHeight + 14}
          textAnchor="middle"
          fontFamily="monospace"
          fontSize="14"
          fill="#000"
        >
          {clean}
        </text>
      )}
    </svg>
  )
}

const BookItems = () => {
  const routes = all_routes
  const currentBranch = useSelector(selectCurrentBranch)
  const { id: bookId } = useParams()

  const [loading, setLoading] = useState(false)
  const [bookDetails, setBookDetails] = useState<Book>()
  const [bookItems, setBookItems] = useState<BookItem[]>([])
  // default: show ALL copies (available + issued + lost + damaged)
  const [status, setStatus] = useState<string>("")
  const [searchText, setSearchText] = useState("")
  const [pagination, setPagination] = useState({ page: 1, limit: 10 })
  const [paginationData, setPaginationData] = useState<{
    currentPage: number
    hasNextPage: boolean
    hasPrevPage: boolean
    limit: number
    total: number
    totalPages: number
  }>()
  const [selectedItem, setSelectedItem] = useState<BookItem | null>(null)
  const [selectedStickerItems, setSelectedStickerItems] = useState<BookItem[]>(
    [],
  )
  const [showBookSticker, setShowBookSticker] = useState(false)
  const barcodeSvgRef = useRef<SVGSVGElement | null>(null)
  const printAreaRef = useRef<HTMLDivElement>(null)
  const componentRef = useRef<HTMLDivElement>(null)
  const stickerRef = useRef<HTMLDivElement | null>(null)

  const [sortBy, setSortBy] = useState(BOOK_ITEMS_SORT_OPTIONS[0])

  // Barcode scanner state
  const [barcodeInput, setBarcodeInput] = useState("")
  const [barcodeResult, setBarcodeResult] = useState<{ item: any; book: any; issue?: any } | null>(null)
  const [returnIssue, setReturnIssue] = useState<any | null>(null)
  const [barcodeLoading, setBarcodeLoading] = useState(false)
  const [barcodeError, setBarcodeError] = useState("")
  const [showCameraModal, setShowCameraModal] = useState(false)
  const html5QrcodeRef = useRef<any>(null)

  // Only set state — useEffect cleanup is the single place that stops the scanner
  const stopCamera = () => setShowCameraModal(false)

  const searchByBarcode = async (value?: string) => {
    const barcode = (value ?? barcodeInput).trim()
    if (!barcode) return
    setBarcodeLoading(true)
    setBarcodeError("")
    setBarcodeResult(null)
    try {
      const res = await getBookItemByBarcode(barcode)
      if (res.success && res.data) {
        setBarcodeResult(res.data)
      } else {
        setBarcodeError("No copy found for this barcode.")
      }
    } catch (err: any) {
      setBarcodeError(err?.message || "Barcode not found.")
    } finally {
      setBarcodeLoading(false)
    }
  }

  const startCameraScanner = () => {
    setBarcodeError("")
    setBarcodeResult(null)
    setShowCameraModal(true)
  }

  const safeStopScanner = (s: any) => {
    if (!s) return
    try {
      s.stop().catch(() => {})
    } catch {}
  }

  // All values inside are stable (state setters + module imports) so [showCameraModal] is complete
  useEffect(() => {
    if (!showCameraModal) return
    let scanner: any = null
    const init = async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode")
        scanner = new Html5Qrcode("book-items-barcode-scanner")
        html5QrcodeRef.current = scanner
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (w: number, h: number) => ({
              width: Math.min(300, Math.round(w * 0.85)),
              height: Math.min(120, Math.round(h * 0.4)),
            }),
          },
          async (val: string) => {
            safeStopScanner(scanner)
            html5QrcodeRef.current = null
            setBarcodeInput(val)
            setShowCameraModal(false)
            setBarcodeLoading(true)
            setBarcodeError("")
            setBarcodeResult(null)
            try {
              const res = await getBookItemByBarcode(val)
              if (res.success && res.data) {
                setBarcodeResult(res.data)
              } else {
                setBarcodeError("No copy found for this barcode.")
              }
            } catch (err: any) {
              setBarcodeError(err?.message || "Barcode not found.")
            } finally {
              setBarcodeLoading(false)
            }
          },
          () => {},
        )
      } catch (err: any) {
        toast.error(err?.message || "Camera access denied. Please allow camera permission.")
        setShowCameraModal(false)
      }
    }
    init()
    return () => {
      safeStopScanner(scanner)
      html5QrcodeRef.current = null
    }
  }, [showCameraModal])

  const fetchData = async () => {
    if (!bookId) return
    setLoading(true)
    try {
      const [bookRes, itemsRes] = await Promise.all([
        getBookById(bookId),
        getBookItems(bookId, {
          status: status,
          page: pagination.page,
          limit: pagination.limit,
          searchText,
        }),
      ])

      if (bookRes?.success) {
        setBookDetails(bookRes.data)
      }

      if (itemsRes?.success) {
        setBookItems(itemsRes.data)
        if (itemsRes.pagination) {
          setPaginationData({
            currentPage: itemsRes.pagination.page,
            total: itemsRes.pagination.total,
            totalPages: itemsRes.pagination.totalPages,
            limit: itemsRes.pagination.limit,
            hasNextPage:
              itemsRes.pagination.page < itemsRes.pagination.totalPages,
            hasPrevPage: itemsRes.pagination.page > 1,
          })
        } else {
          // fallback if backend doesn't send pagination
          setPaginationData(undefined)
        }
      } else {
        setBookItems([])
        setPaginationData(undefined)
      }
    } catch (err: any) {
      setBookItems([])
      setPaginationData(undefined)
      toast.error(err?.message || "Failed to fetch book bookItems")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [bookId, status, pagination.page, pagination.limit, searchText])

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [status, bookId])

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ page, limit: pageSize })
  }

  // Prints the book bookItems list (same pattern as Books page)
  const handlePrintList = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Book Items",
  })

  const handlePrintSticker = useReactToPrint({
    contentRef: stickerRef,
    documentTitle: "Book Stickers",
  })

  const handleExportExcel = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(
        bookItems.map(bookItem => ({
          "Book Item": bookItem.copyIdentifier,
          Barcode: bookItem.barcode,
          Status: bookItem.status,
          Created: bookItem.createdAt,
        })),
      )
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Book Items")
      XLSX.writeFile(wb, `book_items_${bookDetails?.title}.xlsx`)
    } catch (error) {
      toast.error("Error exporting to Excel")
    }
  }

  // Prints the barcode in modal
  const handlePrintBarcode = useReactToPrint({ contentRef: printAreaRef })

  const downloadBarcodeSvg = () => {
    const barcodeValue = getBackendBarcodeValue(selectedItem)
    if (!barcodeValue) return
    const svgEl = barcodeSvgRef.current
    if (!svgEl) return

    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgEl)
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" })
    const url = URL.createObjectURL(blob)

    const a = document.createElement("a")
    a.href = url
    a.download = `${barcodeValue}.svg`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const columns = [
    {
      title: "Copy ID",
      dataIndex: "copyIdentifier",
      render: (text: string) => text || "—",
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
      render: (text: string, record: TableData) => (
        <Link
          to="#"
          className="link-primary"
          data-bs-toggle="modal"
          data-bs-target="#book_item_barcode_modal"
          onClick={() => setSelectedItem(record as any)}
        >
          {getBackendBarcodeValue(record as any) || "—"}
        </Link>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s: string) => {
        const statusStr = String(s || "").toLowerCase()
        const cls =
          statusStr === "available"
            ? "bg-success"
            : statusStr === "issued"
              ? "bg-info"
              : statusStr === "lost"
                ? "bg-danger"
                : "bg-warning"
        return (
          <span className={`badge ${cls}`}>
            {statusStr
              ? statusStr.charAt(0).toUpperCase() + statusStr.slice(1)
              : "N/A"}
          </span>
        )
      },
    },
    {
      title: "Condition",
      dataIndex: "condition",
      render: (c: string) =>
        c ? c.charAt(0).toUpperCase() + c.slice(1) : "—",
    },
    {
      title: "Cabinet",
      dataIndex: "cabinetNo",
      render: (v: string) => v || "—",
    },
    {
      title: "Shelf",
      dataIndex: "shelfNo",
      render: (v: string) => v || "—",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (t: string) => (t ? new Date(t).toLocaleString() : "—"),
    },
  ]

  if (!bookId) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger">Book ID is missing.</div>
          <Link className="btn btn-light" to={routes.libraryBooks}>
            Back to Books
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Book Items</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Management</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.libraryBooks}>Books</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Items
                </li>
              </ol>
            </nav>
          </div>

          <div className="d-flex my-xl-auto right-content align-bookItems-center flex-wrap">
            <TooltipOption
              onRefresh={fetchData}
              onExportPdf={handlePrintList}
              onExportExcel={handleExportExcel}
            />
            <div className="mb-2 me-2">
              <button
                type="button"
                className="btn btn-dark d-flex align-bookItems-center"
                disabled={selectedStickerItems.length === 0}
                onClick={() => {
                  if (selectedStickerItems.length > 0) setShowBookSticker(true)
                }}
              >
                <i className="ti ti-ticket me-2" />
                Stickers
              </button>
            </div>
            <div className="mb-2">
              <Link className="btn btn-primary" to={routes.libraryBooks}>
                <i className="ti ti-arrow-left me-2" />
                Back
              </Link>
            </div>
          </div>
        </div>

        {/* Book Details Card */}
        {bookDetails && (
          <div className="card mb-3">
            <div className="card-body py-3">
              <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                {/* Left: title + fields */}
                <div className="flex-grow-1">
                  <h5 className="fw-bold mb-3">{bookDetails.title}</h5>
                  <div className="d-flex gap-4 flex-wrap">
                    {bookDetails.author && (
                      <div>
                        <div className="text-muted" style={{ fontSize: 11 }}>Author</div>
                        <div className="fw-medium small">{bookDetails.author}</div>
                      </div>
                    )}
                    {bookDetails.publisher && (
                      <div>
                        <div className="text-muted" style={{ fontSize: 11 }}>Publisher</div>
                        <div className="fw-medium small">{bookDetails.publisher}</div>
                      </div>
                    )}
                    {bookDetails.isbn && (
                      <div>
                        <div className="text-muted" style={{ fontSize: 11 }}>ISBN</div>
                        <div className="fw-medium small">{bookDetails.isbn}</div>
                      </div>
                    )}
                    {bookDetails.classificationCode && (
                      <div>
                        <div className="text-muted" style={{ fontSize: 11 }}>DDC Class</div>
                        <div className="fw-medium small">
                          {bookDetails.classificationCode}
                          {bookDetails.subClassificationCode
                            ? ` – ${bookDetails.subClassificationCode}`
                            : ""}
                        </div>
                      </div>
                    )}
                    {bookDetails.cabinetNo && (
                      <div>
                        <div className="text-muted" style={{ fontSize: 11 }}>Cabinet</div>
                        <div className="fw-medium small">{bookDetails.cabinetNo}</div>
                      </div>
                    )}
                    {bookDetails.shelfNo && (
                      <div>
                        <div className="text-muted" style={{ fontSize: 11 }}>Shelf</div>
                        <div className="fw-medium small">{bookDetails.shelfNo}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Right: counters */}
                <div className="d-flex gap-3 flex-shrink-0">
                  <div
                    className="text-center px-4 py-2 rounded"
                    style={{ background: "#e8f5e9", minWidth: 80 }}
                  >
                    <div className="fw-bold fs-4 text-success">
                      {bookDetails.available_quantity ?? 0}
                    </div>
                    <div className="text-success" style={{ fontSize: 11 }}>
                      Available
                    </div>
                  </div>
                  <div
                    className="text-center px-4 py-2 rounded"
                    style={{ background: "#f0f4ff", minWidth: 80 }}
                  >
                    <div className="fw-bold fs-4 text-primary">
                      {bookDetails.quantity ?? 0}
                    </div>
                    <div className="text-primary" style={{ fontSize: 11 }}>
                      Total Copies
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Barcode Scanner */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="input-group" style={{ maxWidth: 360 }}>
                <span className="input-group-text">
                  <i className="ti ti-barcode" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Scan or type barcode..."
                  value={barcodeInput}
                  onChange={e => {
                    setBarcodeInput(e.target.value)
                    setBarcodeResult(null)
                    setBarcodeError("")
                  }}
                  onKeyDown={e => e.key === "Enter" && searchByBarcode()}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => searchByBarcode()}
                  disabled={barcodeLoading}
                >
                  {barcodeLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="ti ti-search" />
                  )}
                </button>
              </div>
              <button
                className="btn btn-outline-secondary"
                title="Scan barcode with camera"
                onClick={startCameraScanner}
              >
                <i className="ti ti-scan me-1" />
                Scan Barcode
              </button>
              {barcodeError && (
                <span className="text-danger small">{barcodeError}</span>
              )}
            </div>
            {barcodeResult && (
              <div className="mt-3 p-4 border rounded-3 bg-light" style={{ fontSize: 15 }}>
                <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
                  <div>
                    <h5 className="fw-bold mb-1 text-dark">
                      <i className="ti ti-book me-2 text-primary" />
                      {barcodeResult.book?.title || "Unknown Book"}
                    </h5>
                    <div className="text-muted" style={{ fontSize: 14 }}>by {barcodeResult.book?.author || "—"}</div>
                  </div>
                  <span
                    style={{ fontSize: 14 }} className={`badge px-3 py-2 ${
                      barcodeResult.item?.status === "available"
                        ? "bg-success"
                        : barcodeResult.item?.status === "issued"
                          ? "bg-info"
                          : barcodeResult.item?.status === "lost"
                            ? "bg-danger"
                            : "bg-warning"
                    }`}
                  >
                    {barcodeResult.item?.status
                      ? barcodeResult.item.status.charAt(0).toUpperCase() +
                        barcodeResult.item.status.slice(1)
                      : "Unknown"}
                  </span>
                </div>
                <div className="row g-4">
                  <div className="col-6 col-md-3">
                    <div className="text-muted mb-1" style={{ fontSize: 13 }}>Publisher</div>
                    <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.book?.publisher || "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted mb-1" style={{ fontSize: 13 }}>ISBN</div>
                    <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.book?.isbn || "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted mb-1" style={{ fontSize: 13 }}>DDC Class</div>
                    <div className="fw-bold" style={{ fontSize: 15 }}>
                      {barcodeResult.book?.classificationCode
                        ? `${barcodeResult.book.classificationCode}${barcodeResult.book.subClassificationCode ? ` – ${barcodeResult.book.subClassificationCode}` : ""}`
                        : "—"}
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted mb-1" style={{ fontSize: 13 }}>Copy ID</div>
                    <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item?.copyIdentifier || "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted mb-1" style={{ fontSize: 13 }}>Condition</div>
                    <div className="fw-bold text-capitalize" style={{ fontSize: 15 }}>{barcodeResult.item?.condition || "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted mb-1" style={{ fontSize: 13 }}>Cabinet</div>
                    <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item?.cabinetNo || "—"}</div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="text-muted mb-1" style={{ fontSize: 13 }}>Shelf</div>
                    <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item?.shelfNo || "—"}</div>
                  </div>
                  {barcodeResult.issue && (
                    <div className="col-12">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Issued To</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>
                        {barcodeResult.issue.student?.name || "—"}
                        {barcodeResult.issue.student?.admission_no && (
                          <span className="text-muted ms-2" style={{ fontSize: 13 }}>({barcodeResult.issue.student.admission_no})</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {barcodeResult.issue && (
                  <div className="mt-3">
                    <button
                      className="btn btn-warning"
                      onClick={() => setReturnIssue(barcodeResult.issue)}
                    >
                      <i className="ti ti-arrow-back-up me-2" />Return This Copy
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Camera Modal */}
        {showCameraModal && (
          <>
            <div
              className="modal fade show d-block"
              role="dialog"
              aria-modal="true"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Scan Barcode</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={stopCamera}
                    />
                  </div>
                  <div className="modal-body p-2">
                    <div id="book-items-barcode-scanner" style={{ width: "100%" }} />
                    <p className="text-muted small mt-2">
                      Point camera at the barcode — it will scan automatically
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={stopCamera}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" />
          </>
        )}

        <div className="card">
          <div className="card-header d-flex align-bookItems-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">Copies</h4>
            <div className="d-flex align-bookItems-center flex-wrap">
              <div className="dropdown mb-3 me-2">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-filter me-2" />
                  Filter
                </Link>
                <ul className="dropdown-menu p-3">
                  {statusOptions.map(opt => (
                    <li key={opt.value || "all"}>
                      <div
                        className={`dropdown-item rounded-1 ${
                          opt.value === status ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setStatus(opt.value)}
                      >
                        {opt.label}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="dropdown mb-3">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-sort-ascending-2 me-2" />
                  Sort by {sortBy.label}
                </Link>
                <ul className="dropdown-menu p-3">
                  {BOOK_ITEMS_SORT_OPTIONS.map((opt, idx) => (
                    <li key={idx}>
                      <div
                        className={`dropdown-item rounded-1 ${
                          opt.label === sortBy.label ? "active" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSortBy(opt)}
                      >
                        {opt.label}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div
            className="card-body p-0 py-3 printable-content"
            ref={componentRef}
          >
            <div className="print-only-header">
              <div className="school-brand">
                <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
              </div>
              <div className="report-title">Book Items</div>
              <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
            </div>
            <Table
              dataSource={bookItems}
              columns={columns}
              pagination={{
                ...paginationData,
                onChange: handleChange,
              }}
              loading={loading}
              setSearchText={setSearchText}
              Selection
              onSelectionChange={(_selectedRowKeys, selectedRows) => {
                setSelectedStickerItems(selectedRows)
              }}
            />
          </div>
        </div>

        {/* Stickers Modal (same approach as Admission Fee Sticker) */}
        {showBookSticker && selectedStickerItems.length > 0 && (
          <>
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
            >
              <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Book Stickers</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowBookSticker(false)}
                    />
                  </div>

                  <div
                    className="modal-body p-3 printable-content"
                    ref={stickerRef}
                    style={{
                      backgroundColor: "#f5f5f5",
                      textAlign: "left",
                    }}
                  >
                    {selectedStickerItems.map(item => (
                      <div
                        key={item._id}
                        className="bg-white border mb-3"
                        style={{
                          width: "100%",
                          maxWidth: "550px",
                          margin: "0 0 15px 0",
                          padding: "12px 15px",
                          fontSize: "13px",
                          lineHeight: "1.5",
                          textAlign: "left",
                          pageBreakInside: "avoid",
                        }}
                      >
                        <div className="d-flex justify-content-between align-bookItems-start mb-2">
                          <div style={{ flex: 1 }}>
                            <div
                              className="fw-bold mb-1"
                              style={{ fontSize: "16px", lineHeight: "1.2" }}
                            >
                              {bookDetails?.title}
                            </div>
                            <div style={{ fontSize: "12px", color: "#333" }}>
                              {bookDetails?.author
                                ? `Author: ${bookDetails.author}`
                                : ""}
                            </div>
                            <div style={{ fontSize: "12px", color: "#333" }}>
                              {bookDetails?.classificationCode
                                ? `DDC: ${bookDetails.classificationCode}${bookDetails.subClassificationCode ? ` - ${bookDetails.subClassificationCode}` : ""}`
                                : ""}
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "12px", color: "#333" }}>
                              Item: {item.copyIdentifier}
                            </div>
                            <div style={{ fontSize: "12px", color: "#333" }}>
                              Barcode: {(item as any).barcode || "—"}
                            </div>
                          </div>
                        </div>

                        <div
                          className="d-flex justify-content-center"
                          style={{ paddingTop: 4 }}
                        >
                          <Code39BarcodeSvg
                            value={String(
                              getBackendBarcodeValue(item) ||
                                item.copyIdentifier ||
                                "",
                            )}
                            height={50}
                            unit={1}
                            showText={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-danger me-2"
                      onClick={() => setShowBookSticker(false)}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handlePrintSticker}
                    >
                      Print
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show" />
          </>
        )}

        {/* Barcode Modal */}
        <div className="modal fade" id="book_item_barcode_modal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Barcode</h4>
                <button
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                {/* Print area (prints exactly what's inside this div) */}
                <div
                  ref={printAreaRef}
                  className="border rounded bg-white p-3 d-flex justify-content-center printable-content"
                >
                  <Code39BarcodeSvg
                    value={
                      getBackendBarcodeValue(selectedItem) ||
                      String((selectedItem as any)?.copyIdentifier || "")
                    }
                    height={70}
                    unit={1.4}
                    showText={true}
                    svgRef={barcodeSvgRef}
                  />
                </div>
              </div>
              <div className="modal-footer d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handlePrintBarcode}
                  disabled={
                    !(
                      getBackendBarcodeValue(selectedItem) ||
                      String((selectedItem as any)?.copyIdentifier || "").trim()
                    )
                  }
                >
                  Print
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={downloadBarcodeSvg}
                  disabled={
                    !(
                      getBackendBarcodeValue(selectedItem) ||
                      String((selectedItem as any)?.copyIdentifier || "").trim()
                    )
                  }
                >
                  Download SVG
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ReturnBookModal
        issue={returnIssue}
        onClose={() => setReturnIssue(null)}
        onSuccess={() => setBarcodeResult(null)}
      />
    </div>
  )
}

export default BookItems
